import React, { useEffect, useState } from 'react';
import { ticketService, Ticket } from '../../services/ticketService';

interface OpenTicketButtonProps {
  sourceType: 'recommendation' | 'anomaly' | 'tag_violation';
  sourceId: string;
  summary: string;
  description: string;
  className?: string;
  label?: string;
  /** Show an existing ticket's link, but never offer to raise a new one.
   *  Used on the per-resource tagging list, where raising tickets one resource
   *  at a time is exactly the behaviour the Remediation tab replaces - see
   *  tag-remediation.service.ts. Rows that already HAVE a ticket still link to
   *  it, because that history is worth keeping visible. */
  viewOnly?: boolean;
}

/**
 * Real Jira ticket creation button, shared across Recommendations and
 * Anomalies. Honestly surfaces "Jira not configured" rather than faking
 * success when JIRA_* env vars aren't set - same pattern as every other
 * real-vs-mock boundary in this app.
 */
export const OpenTicketButton: React.FC<OpenTicketButtonProps> = ({ sourceType, sourceId, summary, description, className, label = 'Raise Ticket', viewOnly = false }) => {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    ticketService.getTicket(sourceType, sourceId)
      .then((t) => setTicket(t))
      .catch(() => {})
      .finally(() => setChecked(true));
  }, [sourceType, sourceId]);

  const handleClick = async () => {
    setLoading(true);
    setError(null);
    try {
      const created = await ticketService.createTicket({ source_type: sourceType, source_id: sourceId, summary, description });
      setTicket(created);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to open ticket');
    } finally {
      setLoading(false);
    }
  };

  const baseClass = className || 'rounded px-3 py-1.5 text-sm font-medium';

  if (ticket?.external_url) {
    return (
      <a
        href={ticket.external_url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${baseClass} text-primary hover:bg-primary/10 dark:text-primary dark:hover:bg-primary/10`}
      >
        View Ticket ({ticket.external_key}) &rarr;
      </a>
    );
  }

  if (viewOnly) return null;

  return (
    <div>
      <button
        onClick={handleClick}
        disabled={loading || !checked}
        className={`${baseClass} text-sky-700 hover:bg-sky-50 disabled:opacity-50 dark:text-sky-300 dark:hover:bg-sky-900/30`}
      >
        {loading ? 'Opening ticket...' : label}
      </button>
      {error && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
    </div>
  );
};
